import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Stack, FormControlLabel, Switch, Paper, Typography, alpha,
  Autocomplete, Chip, InputAdornment,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DocumentGroupsTable from '../../components/documents/DocumentGroupsTable';
import DocumentCodeField from '../../components/documents/DocumentCodeField';
import GroupDocumentsDrawer from '../../components/documents/GroupDocumentsDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { listZones, type OperationalZoneRow } from '../../services/zoneService';
import { SERVICE_HUB_OPTIONS } from '../../constants/partnerServices';
import {
  listDocumentGroups, listDocumentConfigs,
  createDocumentGroup, updateDocumentGroup, deleteDocumentGroup,
  createDocumentConfig, updateDocumentConfig,
  type DocumentGroup, type DocumentConfigItem,
} from '../../services/partnerOnboardingService';
import { ENTITY_TYPES, DOCUMENT_REVIEW_NOTE_SX, DOCUMENT_FORM_HELPER_SX, isLockedPartnerProfileDocument, isLockedPartnerProfileGroup } from '../../constants/documentReviewFlow';
import { PARTNER_SERVICE_CONFIG, getPartnerCreateLabel } from '../../constants/partnerServices';
import {
  groupIdFromRef, groupServiceHubs, groupZoneIds, groupHubsLabel, groupZonesLabel,
  findConflictingDocumentConfig, groupScopeSummary,
  groupMatchesServiceHub, hubLabel, entityLabel, getDocumentGroupFormErrors, slugifyDocumentCode,
} from '../../utils/documentReviewUtils';

const filterFieldSx = {
  minWidth: 160,
  '& .MuiInputLabel-root': { pointerEvents: 'none' },
} as const;

function filterSelectProps(
  value: string,
  emptyLabel: string,
  resolveLabel: (value: string) => string,
) {
  return {
    displayEmpty: true as const,
    renderValue: (selected: unknown) => {
      const key = String(selected ?? '');
      if (!key) return emptyLabel;
      return resolveLabel(key);
    },
  };
}

type GroupFormState = {
  name: string;
  code: string;
  entityType: string;
  description: string;
  zoneIds: string[];
  serviceHubs: string[];
  isActive: boolean;
};

export default function DocumentsManagementPage() {
  const queryClient = useQueryClient();
  const [zoneFilter, setZoneFilter] = useState('');
  const [hubFilter, setHubFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');

  const [groupDialog, setGroupDialog] = useState(false);
  const [docDialog, setDocDialog] = useState(false);
  const [docsDrawerGroup, setDocsDrawerGroup] = useState<DocumentGroup | null>(null);
  const [editingGroup, setEditingGroup] = useState<DocumentGroup | null>(null);
  const [editingDoc, setEditingDoc] = useState<DocumentConfigItem | null>(null);
  const [docContextGroup, setDocContextGroup] = useState<DocumentGroup | null>(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<DocumentGroup | null>(null);
  const [groupFormError, setGroupFormError] = useState('');
  const [groupFieldErrors, setGroupFieldErrors] = useState<{ name?: string; code?: string }>({});
  const [docFormError, setDocFormError] = useState('');
  const [groupAutoCode, setGroupAutoCode] = useState(true);
  const [docAutoCode, setDocAutoCode] = useState(true);
  const listScopeFilters = {
    entityType: entityFilter || undefined,
    zoneId: zoneFilter || undefined,
    serviceHub: hubFilter || undefined,
    includeInactive: true,
  };

  const defaultGroupForm = (): GroupFormState => ({
    name: '',
    code: '',
    entityType: entityFilter || 'driver',
    description: '',
    zoneIds: zoneFilter ? [zoneFilter] : [],
    serviceHubs: hubFilter ? [hubFilter] : [],
    isActive: true,
  });

  const defaultDocForm = (group?: DocumentGroup) => ({
    name: '',
    code: '',
    entityType: group?.entityType || entityFilter || 'driver',
    groupId: group?._id || '',
    isMandatory: true,
    identifierRequired: false,
    verificationRequired: true,
    expiryRequired: false,
    isActive: true,
  });

  const [groupForm, setGroupForm] = useState<GroupFormState>(defaultGroupForm);
  const [docForm, setDocForm] = useState(defaultDocForm());

  const { data: zonesData } = useQuery({ queryKey: ['zones-doc'], queryFn: () => listZones({ limit: 200 }) });
  const zones: OperationalZoneRow[] = zonesData?.rows || [];

  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['document-groups', listScopeFilters],
    queryFn: () => listDocumentGroups(listScopeFilters),
  });

  const { data: allGroups = [] } = useQuery({
    queryKey: ['document-groups', 'all-for-duplicate-check'],
    queryFn: () => listDocumentGroups({ includeInactive: true }),
  });

  const { data: configs = [], isLoading: configsLoading } = useQuery({
    queryKey: ['document-configs', listScopeFilters],
    queryFn: () => listDocumentConfigs(listScopeFilters),
  });

  const { data: allConfigs = [] } = useQuery({
    queryKey: ['document-configs', 'all-for-duplicate-check'],
    queryFn: () => listDocumentConfigs({ includeInactive: true }),
  });

  const hubScopedGroups = useMemo(
    () => (hubFilter
      ? groups.filter((g) => isLockedPartnerProfileGroup(g) || groupMatchesServiceHub(g, hubFilter))
      : groups),
    [groups, hubFilter],
  );

  const visibleGroups = useMemo(
    () => (showInactive
      ? hubScopedGroups.filter((g) => g.isActive === false)
      : hubScopedGroups.filter((g) => g.isActive !== false)),
    [hubScopedGroups, showInactive],
  );

  const countConfigs = useMemo(() => {
    const source = allConfigs.length ? allConfigs : configs;
    const groupIds = new Set(hubScopedGroups.map((g) => g._id));
    if (!hubFilter) return source;
    return source.filter((d) => groupIds.has(groupIdFromRef(d.groupId)));
  }, [allConfigs, configs, hubScopedGroups, hubFilter]);

  const docSearchIndex = useMemo(() => {
    const map = new Map<string, string>();
    countConfigs.forEach((doc) => {
      const gid = groupIdFromRef(doc.groupId);
      if (!gid) return;
      const prev = map.get(gid) || '';
      map.set(gid, `${prev} ${doc.name} ${doc.code}`.toLowerCase());
    });
    return map;
  }, [countConfigs]);

  const searchedGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleGroups;
    return visibleGroups.filter((g) => {
      const blob = [
        g.name,
        g.code,
        g.entityType,
        entityLabel(g.entityType),
        groupZonesLabel(g, zones),
        groupHubsLabel(g),
        groupScopeSummary(g, zones),
        docSearchIndex.get(g._id) || '',
      ].join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [visibleGroups, search, docSearchIndex]);

  const activeDrawerGroup = useMemo(
    () => (docsDrawerGroup ? groups.find((g) => g._id === docsDrawerGroup._id) ?? docsDrawerGroup : null),
    [groups, docsDrawerGroup],
  );

  const selectedGroupZones = useMemo(
    () => zones.filter((z) => groupForm.zoneIds.includes(z._id)),
    [zones, groupForm.zoneIds],
  );

  const allGroupZonesSelected = groupForm.zoneIds.length === 0;
  const displayGroupZones = allGroupZonesSelected ? zones : selectedGroupZones;

  const groupCandidate = useMemo(() => ({
    name: groupForm.name,
    code: groupForm.code,
    entityType: groupForm.entityType,
    zoneIds: groupForm.zoneIds,
    serviceHubs: groupForm.serviceHubs,
  }), [groupForm]);

  const liveGroupFieldErrors = useMemo(
    () => (groupDialog
      ? getDocumentGroupFormErrors(allGroups, groupCandidate, editingGroup?._id)
      : {}),
    [groupDialog, allGroups, groupCandidate, editingGroup?._id],
  );

  useEffect(() => {
    if (!groupDialog || editingGroup || !groupAutoCode) return;
    const next = slugifyDocumentCode(groupForm.name);
    setGroupForm((f) => (f.code === next ? f : { ...f, code: next }));
  }, [groupForm.name, groupAutoCode, groupDialog, editingGroup]);

  useEffect(() => {
    if (!docDialog || editingDoc || !docAutoCode) return;
    const next = slugifyDocumentCode(docForm.name);
    setDocForm((f) => (f.code === next ? f : { ...f, code: next }));
  }, [docForm.name, docAutoCode, docDialog, editingDoc]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['document-groups'] });
    queryClient.invalidateQueries({ queryKey: ['document-configs'] });
  };

  const saveGroupMut = useMutation({
    mutationFn: () => {
      const payload = {
        ...groupForm,
        code: groupForm.code.trim() || slugifyDocumentCode(groupForm.name),
      };
      return editingGroup
        ? updateDocumentGroup(editingGroup._id, payload)
        : createDocumentGroup(payload);
    },
    onSuccess: () => {
      invalidate();
      setGroupDialog(false);
      setEditingGroup(null);
      setGroupFormError('');
      setGroupFieldErrors({});
    },
    onError: (err: Error) => {
      const message = err.message || 'Failed to save group';
      setGroupFormError(message);
      const lower = message.toLowerCase();
      if (lower.includes('code')) {
        setGroupFieldErrors((prev) => ({ ...prev, code: message }));
      } else if (lower.includes('name') || lower.includes('already exists')) {
        setGroupFieldErrors((prev) => ({ ...prev, name: message }));
      }
    },
  });

  const saveDocMut = useMutation({
    mutationFn: () => {
      const payload = {
        ...docForm,
        groupId: docForm.groupId,
        code: docForm.code.trim() || slugifyDocumentCode(docForm.name),
      };
      return editingDoc
        ? updateDocumentConfig(editingDoc._id, payload)
        : createDocumentConfig(payload);
    },
    onSuccess: () => {
      invalidate();
      setDocDialog(false);
      setEditingDoc(null);
      setDocFormError('');
      if (docContextGroup) setDocsDrawerGroup(docContextGroup);
    },
    onError: (err: Error) => setDocFormError(err.message || 'Failed to save document'),
  });

  const deleteGroupMut = useMutation({
    mutationFn: (id: string) => deleteDocumentGroup(id),
    onSuccess: () => { invalidate(); setDeleteGroupTarget(null); setDocsDrawerGroup(null); },
  });

  const toggleGroupMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateDocumentGroup(id, { isActive }),
    onSuccess: (_data, vars) => {
      invalidate();
      setDocsDrawerGroup((current) => (
        current && current._id === vars.id ? { ...current, isActive: vars.isActive } : current
      ));
    },
  });

  const toggleDocMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateDocumentConfig(id, { isActive }),
    onSuccess: invalidate,
  });

  const validateGroupForm = () => {
    const fieldErrors = getDocumentGroupFormErrors(
      allGroups,
      groupCandidate,
      editingGroup?._id,
    );
    if (Object.keys(fieldErrors).length) {
      setGroupFieldErrors(fieldErrors);
      setGroupFormError(fieldErrors.code || fieldErrors.name || 'Fix validation errors before saving.');
      return false;
    }
    setGroupFieldErrors({});
    setGroupFormError('');
    return true;
  };

  const validateDocForm = () => {
    if (!docForm.groupId) {
      setDocFormError('Select a document group first.');
      return false;
    }
    const conflict = findConflictingDocumentConfig(
      allConfigs,
      { name: docForm.name, code: docForm.code, groupId: docForm.groupId },
      editingDoc?._id,
    );
    if (conflict) {
      setDocFormError(
        `Document "${conflict.name}" (${conflict.code}) already exists in this group. Use a unique name and code.`,
      );
      return false;
    }
    setDocFormError('');
    return true;
  };

  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupForm(defaultGroupForm());
    setGroupFormError('');
    setGroupFieldErrors({});
    setGroupAutoCode(true);
    setGroupDialog(true);
  };

  const openManageDocs = (group: DocumentGroup) => setDocsDrawerGroup(group);

  const openAddDoc = (group: DocumentGroup) => {
    if (isLockedPartnerProfileGroup(group)) return;
    setEditingDoc(null);
    setDocContextGroup(group);
    setDocForm(defaultDocForm(group));
    setDocFormError('');
    setDocAutoCode(true);
    setDocDialog(true);
  };

  const openGroupEdit = (g: DocumentGroup) => {
    if (isLockedPartnerProfileGroup(g)) return;
    setEditingGroup(g);
    setGroupForm({
      name: g.name,
      code: g.code,
      entityType: g.entityType,
      description: g.description || '',
      zoneIds: groupZoneIds(g),
      serviceHubs: groupServiceHubs(g),
      isActive: g.isActive,
    });
    setGroupFormError('');
    setGroupFieldErrors({});
    setGroupAutoCode(false);
    setGroupDialog(true);
  };

  const patchGroupForm = (patch: Partial<GroupFormState>) => {
    setGroupForm((prev) => ({ ...prev, ...patch }));
    if (patch.name !== undefined) {
      setGroupFieldErrors((prev) => {
        if (!prev.name) return prev;
        const next = { ...prev };
        delete next.name;
        return next;
      });
    }
    if (patch.code !== undefined) {
      setGroupFieldErrors((prev) => {
        if (!prev.code) return prev;
        const next = { ...prev };
        delete next.code;
        return next;
      });
    }
    setGroupFormError('');
  };

  const openDocEdit = (d: DocumentConfigItem) => {
    if (isLockedPartnerProfileDocument(d)) return;
    setEditingDoc(d);
    const group = groups.find((g) => g._id === groupIdFromRef(d.groupId));
    setDocContextGroup(group || docsDrawerGroup);
    setDocForm({
      name: d.name,
      code: d.code,
      entityType: d.entityType,
      groupId: groupIdFromRef(d.groupId) || '',
      isMandatory: d.isMandatory,
      identifierRequired: !!d.identifierRequired,
      verificationRequired: d.verificationRequired,
      expiryRequired: d.expiryRequired,
      isActive: d.isActive !== false,
    });
    setDocFormError('');
    setDocAutoCode(false);
    setDocDialog(true);
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Configuration"
        title="Documents"
        subtitle="Configure document groups and types partners must upload during onboarding. Profile Photo is a default for every partner and service — it cannot be edited or deactivated."
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search groups, codes, partner type, zones, or document names…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 480 }}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            select
            size="small"
            label="Partner type"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={filterSelectProps(entityFilter, 'All types', getPartnerCreateLabel)}
            sx={{ ...filterFieldSx, minWidth: 200 }}
          >
            <MenuItem value="">All types</MenuItem>
            {PARTNER_SERVICE_CONFIG.map((c) => (
              <MenuItem key={c.key} value={c.key}>
                {getPartnerCreateLabel(c.key)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Zone"
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={filterSelectProps(zoneFilter, 'All zones', (id) => {
              const zone = zones.find((z) => z._id === id);
              return zone ? `${zone.name} (${zone.code})` : id;
            })}
            sx={{ ...filterFieldSx, minWidth: 180 }}
          >
            <MenuItem value="">All zones</MenuItem>
            {zones.map((z) => <MenuItem key={z._id} value={z._id}>{z.name} ({z.code})</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Service hub"
            value={hubFilter}
            onChange={(e) => setHubFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={filterSelectProps(hubFilter, 'All hubs', hubLabel)}
            sx={{ ...filterFieldSx, minWidth: 180 }}
          >
            <MenuItem value="">All hubs</MenuItem>
            {SERVICE_HUB_OPTIONS.map((h) => <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>)}
          </TextField>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: { xs: 'flex-start', md: 'center' },
              px: 1.25,
              py: 0.5,
              borderRadius: 2,
              border: 1,
              borderColor: showInactive ? 'warning.main' : 'divider',
              bgcolor: showInactive ? (theme) => alpha(theme.palette.warning.main, 0.08) : 'background.paper',
              flexShrink: 0,
            }}
          >
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              )}
              label={(
                <Box sx={{ ml: 0.25 }}>
                  <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                    Show inactive only
                  </Typography>
                  <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                    {showInactive ? 'Deactivated groups & documents' : 'Active groups & documents'}
                  </Typography>
                </Box>
              )}
              sx={{
                m: 0,
                alignItems: 'center',
                gap: 0.75,
                '& .MuiFormControlLabel-label': { ml: 0.5 },
              }}
            />
          </Box>
          <Button
            size="small"
            variant="contained"
            onClick={openAddGroup}
            startIcon={
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '6px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.18)',
                }}
              >
                <Add sx={{ fontSize: 16 }} />
              </Box>
            }
            sx={{
              ml: { md: 'auto' },
              height: 40,
              px: 1.75,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '& .MuiButton-startIcon': { m: 0, mr: 1 },
            }}
          >
            Add group
          </Button>
          </Stack>
        </Stack>
      </Paper>

      <DocumentGroupsTable
        groups={searchedGroups}
        zones={zones}
        countConfigs={countConfigs}
        loading={groupsLoading}
        emptyMessage={
          search.trim()
            ? `No groups match "${search.trim()}".`
            : hubFilter
              ? `No ${showInactive ? 'inactive' : 'active'} groups for ${hubLabel(hubFilter)} hub.`
              : (showInactive ? 'No inactive document groups match the current filters.' : 'No active document groups match the current filters.')
        }
        onManageDocs={openManageDocs}
        onEditGroup={openGroupEdit}
        onToggleGroup={(id, isActive) => toggleGroupMut.mutate({ id, isActive })}
        onDeactivateGroup={setDeleteGroupTarget}
      />

      <GroupDocumentsDrawer
        open={!!docsDrawerGroup}
        group={activeDrawerGroup}
        zones={zones}
        configs={allConfigs.length ? allConfigs : configs}
        inactiveOnly={showInactive}
        loading={configsLoading}
        onClose={() => setDocsDrawerGroup(null)}
        onAddDoc={openAddDoc}
        onEditDoc={openDocEdit}
        onToggleDoc={(id, isActive) => toggleDocMut.mutate({ id, isActive })}
        onToggleGroup={(id, isActive) => toggleGroupMut.mutate({ id, isActive })}
      />

      <ConfirmDialog
        open={!!deleteGroupTarget}
        title="Deactivate document group?"
        message={`Deactivate "${deleteGroupTarget?.name}"? All documents in this group will be deactivated and hidden from partner upload.`}
        confirmLabel="Deactivate"
        confirmColor="warning"
        onCancel={() => setDeleteGroupTarget(null)}
        onConfirm={() => deleteGroupTarget && deleteGroupMut.mutate(deleteGroupTarget._id)}
        loading={deleteGroupMut.isPending}
      />

      <Dialog open={groupDialog} onClose={() => setGroupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Edit document group' : 'New document group'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              required
              value={groupForm.name}
              error={!!(groupFieldErrors.name || liveGroupFieldErrors.name)}
              helperText={groupFieldErrors.name || liveGroupFieldErrors.name || ' '}
              onChange={(e) => patchGroupForm({ name: e.target.value })}
            />
            <DocumentCodeField
              value={groupForm.code}
              name={groupForm.name}
              error={groupFieldErrors.code || liveGroupFieldErrors.code}
              disabled={!!editingGroup}
              autoCode={groupAutoCode}
              onAutoCodeChange={setGroupAutoCode}
              onChange={(code) => patchGroupForm({ code })}
            />
            <TextField select label="Partner type" fullWidth value={groupForm.entityType} onChange={(e) => patchGroupForm({ entityType: e.target.value })}>
              {ENTITY_TYPES.map((t) => <MenuItem key={t} value={t}>{getPartnerCreateLabel(t)}</MenuItem>)}
            </TextField>
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              options={zones}
              value={displayGroupZones}
              getOptionLabel={(z) => `${z.name} (${z.code})`}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              onChange={(_, value) => {
                if (!zones.length || value.length === zones.length) {
                  patchGroupForm({ zoneIds: [] });
                  return;
                }
                patchGroupForm({ zoneIds: value.map((z) => z._id) });
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option._id}
                    label={option.name ? `${option.name} (${option.code})` : option.code}
                    size="small"
                    color={allGroupZonesSelected ? 'primary' : 'default'}
                    variant={allGroupZonesSelected ? 'outlined' : 'filled'}
                    sx={{ height: 24, '& .MuiChip-label': { lineHeight: 1.25 } }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Zones"
                  placeholder={displayGroupZones.length ? '' : 'All zones'}
                  helperText={
                    allGroupZonesSelected
                      ? (zones.length
                        ? `Applies to all zones (${zones.map((z) => z.name).join(', ')})`
                        : 'Applies to all zones')
                      : `${selectedGroupZones.length} zone${selectedGroupZones.length === 1 ? '' : 's'} selected`
                  }
                  sx={{
                    '& .MuiInputBase-root': {
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      py: 0.75,
                    },
                    '& .MuiAutocomplete-tag': { m: 0 },
                  }}
                />
              )}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              options={SERVICE_HUB_OPTIONS}
              value={SERVICE_HUB_OPTIONS.filter((h) => groupForm.serviceHubs.includes(h.value))}
              getOptionLabel={(h) => h.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              onChange={(_, value) => patchGroupForm({ serviceHubs: value.map((h) => h.value) })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.value} label={option.label} size="small" />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Service hubs"
                  helperText="Leave empty for all service hubs"
                />
              )}
            />
            <TextField label="Description" fullWidth multiline rows={2} value={groupForm.description} onChange={(e) => patchGroupForm({ description: e.target.value })} />
            <FormControlLabel
              control={<Switch checked={groupForm.isActive} onChange={(e) => patchGroupForm({ isActive: e.target.checked })} />}
              label={groupForm.isActive ? 'Active — group available for uploads' : 'Inactive — group hidden from partners'}
            />
            {groupFormError && !groupFieldErrors.name && !groupFieldErrors.code && (
              <Typography variant="body2" color="error">{groupFormError}</Typography>
            )}
          </Stack>        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { if (validateGroupForm()) saveGroupMut.mutate(); }}
            disabled={
              !groupForm.name.trim()
              || !(groupForm.code.trim() || slugifyDocumentCode(groupForm.name))
              || !!liveGroupFieldErrors.name
              || !!liveGroupFieldErrors.code
              || saveGroupMut.isPending
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={docDialog} onClose={() => setDocDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDoc ? 'Edit document type' : 'New document type'}
          {docContextGroup && (
            <Typography variant="body2" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, mt: 0.75 }} display="block">
              Group: {docContextGroup.name} · {groupZonesLabel(docContextGroup, zones)} · {groupHubsLabel(docContextGroup)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={DOCUMENT_FORM_HELPER_SX}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={DOCUMENT_REVIEW_NOTE_SX}>
              {editingDoc
                ? 'Update how this document type appears and what partners must provide when uploading.'
                : 'Add a document type within the selected group. Partners will see active documents during onboarding upload.'}
            </Typography>
            <TextField
              label="Document name"
              fullWidth
              required
              value={docForm.name}
              helperText="Display name shown to partners, e.g. Driving licence"
              onChange={(e) => setDocForm({ ...docForm, name: e.target.value })}
            />
            <DocumentCodeField
              label="Document code"
              value={docForm.code}
              name={docForm.name}
              disabled={!!editingDoc}
              autoCode={docAutoCode}
              onAutoCodeChange={setDocAutoCode}
              onChange={(code) => setDocForm({ ...docForm, code })}
            />
            <Stack spacing={1.25}>
              <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <FormControlLabel
                  control={<Switch checked={docForm.isMandatory} onChange={(e) => setDocForm({ ...docForm, isMandatory: e.target.checked })} />}
                  label="Mandatory"
                />
                <Typography variant="body2" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                  {docForm.isMandatory
                    ? 'Partners must upload this document before onboarding can complete.'
                    : 'Optional — partners may skip this document during upload.'}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <FormControlLabel
                  control={<Switch checked={docForm.identifierRequired} onChange={(e) => setDocForm({ ...docForm, identifierRequired: e.target.checked })} />}
                  label="Document number required"
                />
                <Typography variant="body2" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                  {docForm.identifierRequired
                    ? 'Partners must enter a document ID or reference number when uploading.'
                    : 'File upload only — no document number field on the partner form.'}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <FormControlLabel
                  control={<Switch checked={docForm.expiryRequired} onChange={(e) => setDocForm({ ...docForm, expiryRequired: e.target.checked })} />}
                  label="Expiry required"
                />
                <Typography variant="body2" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                  {docForm.expiryRequired
                    ? 'Partners must provide an expiry date for this document.'
                    : 'No expiry date is collected for this document type.'}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <FormControlLabel
                  control={<Switch checked={docForm.verificationRequired} onChange={(e) => setDocForm({ ...docForm, verificationRequired: e.target.checked })} />}
                  label="Admin verification required"
                />
                <Typography variant="body2" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                  {docForm.verificationRequired
                    ? 'Uploaded files stay pending until an admin approves them.'
                    : 'Uploads are accepted without manual admin review.'}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <FormControlLabel
                  control={<Switch checked={docForm.isActive} onChange={(e) => setDocForm({ ...docForm, isActive: e.target.checked })} />}
                  label={docForm.isActive ? 'Active — visible to partners' : 'Inactive — hidden from partners'}
                />
                <Typography variant="body2" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, mt: 0.5, pl: 4.5 }}>
                  {docForm.isActive
                    ? 'Partners can see and upload this document during onboarding.'
                    : 'Hidden from partner upload — turn on when ready to collect this document.'}
                </Typography>
              </Box>
            </Stack>
            {docFormError && (
              <Typography variant="body2" color="error">{docFormError}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { if (validateDocForm()) saveDocMut.mutate(); }}
            disabled={
              !docForm.name.trim()
              || !(docForm.code.trim() || slugifyDocumentCode(docForm.name))
              || !docForm.groupId
              || saveDocMut.isPending
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
